// Competência mensal das recorrências financeiras e das faturas de cartão: inteiro AAAAMM (202611),
// como o backend manda (`start_competence`, `end_competence`, `skips`). Rótulo curto "Nov/2026".
// Tudo por aritmética de inteiros — nunca `new Date('AAAA-MM-DD')` (ver utils/formatters.ts).
import { pad2 } from '@components/dateInputUtils';
import { shiftMonth, type YearMonth } from './period';

/** Competência AAAAMM (ex.: 202611). */
export type Competence = number;

const SHORT_MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** `{ year: 2026, month: 11 }` → 202611. */
export const competenceFromYearMonth = ({ year, month }: YearMonth): Competence => year * 100 + month;

/** 202611 → `{ year: 2026, month: 11 }`. */
export const competenceToYearMonth = (c: Competence): YearMonth => ({ year: Math.floor(c / 100), month: c % 100 });

/** Inteiro AAAAMM com mês 1..12 e ano plausível. */
export const isValidCompetence = (c: unknown): c is Competence => {
  if (typeof c !== 'number' || !Number.isInteger(c)) return false;
  const { year, month } = competenceToYearMonth(c);
  return month >= 1 && month <= 12 && year >= 1900 && year <= 9999;
};

/** 202611 → "Nov/2026" ('' se inválida). */
export const competenceLabel = (c?: Competence | null): string => {
  if (c === undefined || c === null || !isValidCompetence(c)) return '';
  const { year, month } = competenceToYearMonth(c);
  return `${SHORT_MONTHS[month - 1]}/${year}`;
};

/** Soma `delta` meses (pode ser negativo): 202611 + 2 → 202701. */
export const addCompetence = (c: Competence, delta: number): Competence => competenceFromYearMonth(shiftMonth(competenceToYearMonth(c), delta));

/** Competência do mês corrente pelo relógio local. */
export const currentCompetence = (now: Date = new Date()): Competence => competenceFromYearMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });

/** Competência de uma data "AAAA-MM-DD" (ou carimbo); null se inválida. */
export const competenceOf = (iso?: string | null): Competence | null => {
  const m = /^(\d{4})-(\d{2})/.exec(iso ?? '');
  if (!m) return null;
  const c = Number(m[1]) * 100 + Number(m[2]);
  return isValidCompetence(c) ? c : null;
};

/** 202611 → "2026-11" (formato dos parâmetros `from`/`to` e dos `months` da projeção). */
export const competenceToKey = (c: Competence): string => {
  const { year, month } = competenceToYearMonth(c);
  return `${year}-${pad2(month)}`;
};

/** "2026-11" → 202611; null se inválida. */
export const competenceFromKey = (key?: string | null): Competence | null => competenceOf(key ? `${key}-01` : null);

/** "2026-11" → "Nov/2026" (devolve a chave se não reconhecer). */
export const monthKeyLabel = (key: string): string => {
  const c = competenceFromKey(key);
  return c ? competenceLabel(c) : key;
};

/**
 * Competência da fatura de uma compra (espelho de `domain.CardBillingCompetence`): compra no dia
 * `>= closing_day` cai na fatura do mês seguinte. null se a data for inválida.
 */
export const cardCompetence = (closingDay: number, purchaseISO: string): Competence | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(purchaseISO);
  if (!m) return null;
  const c = competenceOf(purchaseISO);
  if (c === null) return null;
  return Number(m[3]) >= closingDay ? addCompetence(c, 1) : c;
};

/** Lista de competências de `from` a `to` (inclusive), em ordem; vazia se `to < from`. */
export const competenceRange = (from: Competence, to: Competence): Competence[] => {
  const out: Competence[] = [];
  for (let c = from; c <= to && out.length < 600; c = addCompetence(c, 1)) out.push(c);
  return out;
};
